import React, { FC } from 'react';
import {
  Button,
  FormGroup,
  Input,
  InputGroup,
  InputGroupText,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import * as OrderSummary from '../../../../../shared/orders/model';
import { Currency } from '../../../../components/Currency';
import { JSONForm } from '../../../../components/json-form/JSONForm';
import { useTranslation } from '../../../../translations';
import { useConfig } from '../../../../utils';

interface CreateVoucherModalProps {
  readonly order: OrderSummary.Order;
  readonly onSuccess: () => void;
  readonly onCancel: () => void;
}

export const OrderCreateVoucherModal: FC<CreateVoucherModalProps> = ({
  order,
  onCancel,
  onSuccess,
}) => {
  const { ts } = useTranslation();
  const { currencySymbol } = useConfig();
  return (
    <Modal className="modal-large" id="createVoucherModal" isOpen>
      <JSONForm
        method="post"
        onSuccess={onSuccess}
        path={`/api/orders/${order.id}/vouchers/create`}
      >
        <ModalHeader>{ts('voucher_modal.create_voucher_orderid', order.id)}</ModalHeader>
        <ModalBody>
          <p>{ts('voucher_modal.create_a_onetime_use_voucher')}</p>
          <FormGroup>
            <Label for="displayName">
              {ts('common.display_name')}
              <small>{ts('common.optional')}</small>
            </Label>
            <Input id="displayName" name="displayName" />
          </FormGroup>
          <FormGroup>
            <Label for="name">{ts('common.amount')}</Label>
            <InputGroup>
              <InputGroupText>{currencySymbol}</InputGroupText>
              <Input id="amount" min={0.01} name="amount" required step={0.01} type="number" />
            </InputGroup>
          </FormGroup>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCancel}>
            {ts('common.cancel')}
          </Button>{' '}
          <Button color="primary" id="createVoucherOrder" type="submit">
            {ts('common.create')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};

interface DeleteVoucherModalProps {
  readonly order: OrderSummary.Order;
  readonly voucherId: number;
  readonly onSuccess: () => void;
  readonly onCancel: () => void;
}

export const DeteteOrderVoucherModal: FC<DeleteVoucherModalProps> = ({
  order,
  voucherId,
  onCancel,
  onSuccess,
}) => {
  const { ts } = useTranslation();
  const voucher = order.vouchers.find((x) => x.id === voucherId);

  if (!voucher) {
    return null;
  }

  return (
    <Modal className="modal-large" id="deleteVoucherModal" isOpen>
      <JSONForm
        method="delete"
        onSuccess={onSuccess}
        path={`/api/orders/${order.id}/vouchers/${voucher.id!}`}
      >
        <ModalHeader>{ts('voucher_modal.delete_voucher_for_orderid', order.id)}</ModalHeader>
        <ModalBody>
          <p>
            {ts('voucher_modal.are_you_sure_you_want_to_delete_the_voucher_valued')}{' '}
            <Currency value={voucher.amount} />?
          </p>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={onCancel}>
            {ts('common.cancel')}
          </Button>{' '}
          <Button color="danger" id="deleteVoucherOrder" type="submit">
            {ts('common.delete')}
          </Button>
        </ModalFooter>
      </JSONForm>
    </Modal>
  );
};
